import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { ArrowLeft, Package } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { MaskTitle } from "@/components/MaskTitle";
import { ItemCard } from "@/components/ItemCard";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { SelectButton } from "@/components/SelectButton";
import { QuoteDialog } from "@/components/QuoteDialog";
import { isSampleable } from "@/lib/samples";
import { CompareButton } from "@/components/Compare";
import { isComparable } from "@/context/CompareContext";
import { FinishChips } from "@/components/FinishDots";

export default function ItemPage() {
  const { id } = useParams();
  const [active, setActive] = useState(0);
  const [quote, setQuote] = useState(false);
  const [samples, setSamples] = useState(false);
  const { data: item, error } = useSWR(`/items/${id}`, fetcher);
  const { data: catData } = useSWR("/categories", fetcher);
  const { data: relData } = useSWR(item?.category ? `/items?category=${item.category}` : null, fetcher);
  const cat = asList(catData).find((c) => c.slug === item?.category);
  const related = asList(relData).filter((r) => r.id !== id).slice(0, 3);
  const images = asList(item?.images);
  const sampleable = isSampleable(item);

  if (error) return <main className="pt-40 text-center font-display text-3xl text-[#A09E97]">Item not found.</main>;
  if (!item) return <main className="pt-40 min-h-screen" />;

  return (
    <main className="pt-28 mx-auto max-w-[1440px] px-5 sm:px-10">
      <Link to={`/c/${item.category}`} data-testid="item-back-link"
        className="inline-flex items-center gap-2 font-mono-j text-xs uppercase tracking-[0.25em] text-[#A09E97] hover:text-[#E5C158] transition-colors">
        <ArrowLeft className="w-4 h-4" /> {cat?.name || "Back"}
      </Link>
      <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 min-w-0">
          <div className="relative aspect-[4/5] sm:aspect-[5/4] overflow-hidden border border-[#D4AF37]/20 bg-[#13141C]" data-testid="item-main-image">
            {images.map((src, i) => (
              <motion.img key={src + i} src={imgUrl(src)} alt={item.name} initial={false}
                animate={{ opacity: i === active ? 1 : 0, scale: i === active ? 1 : 1.05 }} transition={{ duration: 0.9 }}
                className="absolute inset-0 w-full h-full object-cover" />
            ))}
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {images.map((src, i) => (
                <button key={src + i} onClick={() => setActive(i)} data-testid={`item-thumb-${i}`}
                  className={`aspect-square overflow-hidden border transition-colors ${i === active ? "border-[#D4AF37]" : "border-[#D4AF37]/15 opacity-60 hover:opacity-100"}`}>
                  <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start min-w-0">
          <Eyebrow>{item.code || cat?.name}</Eyebrow>
          <MaskTitle text={item.name} className="mt-5 font-display font-light text-4xl sm:text-6xl text-[#F5F3EF] leading-[1] break-words" />
          {item.supplier && <p className="mt-4 text-[#E5C158]" data-testid="item-supplier">{item.supplier}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setQuote(true)} data-testid="item-quote-btn"
              className="h-12 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors duration-300">
              Request a quote
            </button>
            {sampleable && (
              <button onClick={() => setSamples(true)} data-testid="item-samples-btn"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-6 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors duration-300">
                <Package className="w-4 h-4" /> Request a sample
              </button>
            )}
            <SelectButton item={item} variant="full" />
            {isComparable(item) && <CompareButton item={item} full />}
            {isComparable(item) && (
              <Link to={`/pairing?${item.category === "board-products" ? "board" : "stone"}=${item.id}`} data-testid="item-pair-btn"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-6 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors duration-300">
                Pair with {item.category === "board-products" ? "a stone" : "a board"}
              </Link>
            )}
          </div>
          <FinishChips item={item} />
          {item.description && <p className="mt-8 text-[#A09E97] leading-relaxed whitespace-pre-line [overflow-wrap:anywhere]">{item.description}</p>}
          {asList(item.specs).length > 0 && (
            <dl className="mt-10 border-t border-[#D4AF37]/20" data-testid="item-specs">
              {item.specs.map((s, i) => (
                <div key={i} className="flex justify-between gap-6 py-4 border-b border-[#D4AF37]/15">
                  <dt className="font-mono-j text-xs uppercase tracking-[0.2em] text-[#A09E97]">{s.label}</dt>
                  <dd className="text-[#F5F3EF] text-right">{s.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
      <QuoteDialog open={quote} onClose={() => setQuote(false)} items={[item]} />
      <QuoteDialog open={samples} onClose={() => setSamples(false)} mode="samples" items={[item]} />
      {related.length > 0 && (
        <section className="pt-32">
          <Reveal><h2 className="font-display text-4xl text-[#F5F3EF] mb-12">More from <em className="text-[#E5C158]">{cat?.name}</em></h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((r, i) => <ItemCard key={r.id} item={r} i={i} />)}
          </div>
        </section>
      )}
    </main>
  );
}
